import { useEffect, useState } from "react";
import { Settings as SettingsIcon, ShieldCheck, Languages, BookOpen, Download } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { useI18n } from "@/context/I18nContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ROLE_LABELS, SIZE_LABELS } from "@/lib/strings";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function Settings() {
  const { user } = useAuth();
  const { memberships, companyId, can } = useTenant();
  const { t, language, languages, setLanguage, fmtDate } = useI18n();
  const [downloading, setDownloading] = useState("");
  const [plan, setPlan] = useState(null);
  const [subData, setSubData] = useState(null);
  const [paidPlans, setPaidPlans] = useState([]);
  const [billingInterval, setBillingInterval] = useState("month");
  const [checkoutBusy, setCheckoutBusy] = useState("");
  const isTenantAdmin = memberships.some(
    (m) => m.company_id === companyId && m.role === "ADMIN_AZIENDA");

  useEffect(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/subscription`)
      .then(({ data }) => setSubData(data))
      .catch(() => {});
    api.get("/payments/plans")
      .then(({ data }) => setPaidPlans(data.plans || []))
      .catch(() => {});
  }, [companyId]);

  const startCheckout = async (planCode) => {
    setCheckoutBusy(planCode);
    try {
      const { data } = await api.post(`/companies/${companyId}/subscription/checkout`, {
        plan_code: planCode, interval: billingInterval, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(t("settings.subscription.checkout_error"), { description: formatApiError(e) });
      setCheckoutBusy("");
    }
  };

  useEffect(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}`)
      .then(({ data }) => setPlan(data.plan || null))
      .catch(() => {});
  }, [companyId]);

  const changeLanguage = async (code) => {
    try {
      await setLanguage(code);
      toast.success(t("settings.language.saved"));
    } catch (e) {
      toast.error(t("common.error"), { description: formatApiError(e) });
    }
  };

  const downloadManual = async (audience, lang) => {
    setDownloading(`${audience}-${lang}`);
    try {
      const { data } = await api.get(`/companies/${companyId}/help/manual`, {
        params: { language: lang, audience }, responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `esg-pmi-manuale-${audience.toLowerCase()}-${lang}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t("common.error"), { description: formatApiError(e) });
    } finally {
      setDownloading("");
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
          <SettingsIcon className="w-7 h-7 text-green-700" aria-hidden="true" />
          {t("settings.title")}
        </h1>
        <p className="text-sm text-slate-600 mt-1">{t("settings.subtitle")}</p>
      </div>

      <Card className="border-slate-200" data-testid="settings-plan-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{t("settings.subscription.title")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] text-slate-500">{t("settings.subscription.current")}</p>
              <p className="text-lg font-bold text-slate-900">
                {plan?.name || t("settings.subscription.none")}
              </p>
              {subData?.subscription?.status && (
                <p className="text-xs text-slate-500 mt-0.5">
                  {t("settings.subscription.status")}: {subData.subscription.status}
                  {subData.subscription.interval === "year"
                    ? ` · ${t("settings.subscription.yearly")}`
                    : ` · ${t("settings.subscription.monthly")}`}
                </p>
              )}
            </div>
            {plan && (
              <Badge variant="outline" className="bg-green-50 text-green-800 border-green-300"
                data-testid="settings-plan-badge">{plan.code}</Badge>
            )}
          </div>
          {isTenantAdmin && paidPlans.length > 0 && (
            <div className="space-y-3 border-t border-slate-100 pt-3">
              <div className="flex items-center gap-2">
                <Select value={billingInterval} onValueChange={setBillingInterval}>
                  <SelectTrigger className="w-40 h-8 text-xs" data-testid="billing-interval-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="month" data-testid="billing-interval-month">
                      {t("settings.subscription.monthly")}</SelectItem>
                    <SelectItem value="year" data-testid="billing-interval-year">
                      {t("settings.subscription.yearly")}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500">{t("settings.subscription.manage_hint")}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="plans-grid">
                {paidPlans.map((p) => {
                  const isCurrent = plan?.code === p.code;
                  const isDowngrade = plan && (p.sort_order ?? 0) <= (plan.sort_order ?? 0);
                  const price = billingInterval === "year"
                    ? p.price_yearly_cents : p.price_monthly_cents;
                  return (
                    <div key={p.code}
                      className={`rounded-md border p-3 ${isCurrent ? "border-green-300 bg-green-50" : "border-slate-200"}`}
                      data-testid={`plan-card-${p.code}`}>
                      <p className="text-sm font-semibold text-slate-900">{p.name}</p>
                      {price != null && (
                        <p className="text-xs text-slate-600 mt-0.5">
                          €{(price / 100).toLocaleString("it-IT")}/
                          {billingInterval === "year"
                            ? t("settings.subscription.yearly")
                            : t("settings.subscription.monthly")}
                        </p>
                      )}
                      {isCurrent ? (
                        <Badge className="mt-2 bg-green-100 text-green-800"
                          data-testid={`plan-current-${p.code}`}>{t("settings.subscription.current")}</Badge>
                      ) : (
                        <Button size="sm"
                          className="mt-2 h-7 text-xs bg-green-700 hover:bg-green-800 text-white"
                          disabled={isDowngrade || checkoutBusy === p.code || !p.stripe_lookup_monthly}
                          onClick={() => startCheckout(p.code)}
                          data-testid={`plan-upgrade-${p.code}`}>
                          {checkoutBusy === p.code ? "…" : t("settings.subscription.choose")}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-slate-200" data-testid="settings-language-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Languages className="w-4 h-4 text-green-700" aria-hidden="true" /> {t("settings.language.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-slate-600 leading-relaxed">{t("settings.language.text")}</p>
          <div className="flex items-center gap-3">
            <Select value={language} onValueChange={changeLanguage}>
              <SelectTrigger className="w-52" data-testid="settings-language-select"
                aria-label={t("language.label")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {languages.map((l) => (
                  <SelectItem key={l.code} value={l.code}
                    data-testid={`settings-language-option-${l.code}`}>
                    {l.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-slate-500">
              {t("language.current")}: <strong>{languages.find((l) => l.code === language)?.name || language}</strong>
            </span>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200" data-testid="settings-manual-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-green-700" aria-hidden="true" /> {t("settings.manual.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-slate-600 leading-relaxed">{t("settings.manual.text")}</p>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" disabled={!companyId || !!downloading}
              onClick={() => downloadManual("CLIENT", "it")} data-testid="manual-client-it">
              <Download className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> {t("settings.manual.clientIt")}
            </Button>
            <Button variant="outline" size="sm" disabled={!companyId || !!downloading}
              onClick={() => downloadManual("CLIENT", "en")} data-testid="manual-client-en">
              <Download className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> {t("settings.manual.clientEn")}
            </Button>
            {(can("analysis:score:view") || user?.is_superadmin) && (
              <>
                <Button variant="outline" size="sm" disabled={!companyId || !!downloading}
                  onClick={() => downloadManual("CONSULTANT", "it")} data-testid="manual-consultant-it">
                  <Download className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> {t("settings.manual.consultantIt")}
                </Button>
                <Button variant="outline" size="sm" disabled={!companyId || !!downloading}
                  onClick={() => downloadManual("CONSULTANT", "en")} data-testid="manual-consultant-en">
                  <Download className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> {t("settings.manual.consultantEn")}
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200" data-testid="settings-profile-card">
        <CardHeader><CardTitle className="text-base">{t("settings.profile")}</CardTitle></CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div><dt className="text-slate-500 text-xs">{t("settings.name")}</dt><dd className="font-medium text-slate-900">{user?.full_name}</dd></div>
            <div><dt className="text-slate-500 text-xs">Email</dt><dd className="font-medium text-slate-900">{user?.email}</dd></div>
            <div>
              <dt className="text-slate-500 text-xs">{t("settings.platformLevel")}</dt>
              <dd>{user?.is_superadmin
                ? <Badge variant="outline" className="bg-green-50 text-green-800 border-green-300">Super Admin</Badge>
                : <Badge variant="outline"
                    className={user?.platform_level === "CONSULENTE"
                      ? "bg-blue-50 text-blue-800 border-blue-300" : "bg-slate-50"}
                    data-testid="platform-level-badge">
                    {t(`settings.platformLevels.${user?.platform_level}`) || t("settings.companyUser")}
                  </Badge>}
              </dd>
            </div>
            <div><dt className="text-slate-500 text-xs">{t("settings.accountCreated")}</dt><dd className="font-medium text-slate-900">{fmtDate(user?.created_at)}</dd></div>
          </dl>
        </CardContent>
      </Card>

      <Card className="border-slate-200" data-testid="settings-memberships-card">
        <CardHeader><CardTitle className="text-base">{t("settings.memberships")}</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {memberships.map((m) => (
            <div key={m.company_id} className="flex flex-wrap items-center justify-between gap-2 border border-slate-200 rounded-md p-3"
              data-testid={`membership-${m.company?.denominazione?.slice(0, 12)}`}>
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-slate-900">{m.company?.denominazione}</p>
                {m.company?.is_demo && <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300 text-[10px]">{t("common.demo")}</Badge>}
                {m.company?.dimensione_pmi && (
                  <Badge variant="outline" className="bg-green-50 text-green-800 border-green-300 text-[10px]">
                    {SIZE_LABELS[m.company.dimensione_pmi]}
                  </Badge>
                )}
              </div>
              <Badge variant="outline" className="bg-slate-50">{t(`roleLabels.${m.role}`) || ROLE_LABELS[m.role] || m.role}</Badge>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="border-slate-200" data-testid="settings-security-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-green-700" aria-hidden="true" /> {t("settings.security")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600 leading-relaxed">
            {t("settings.securityText")}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
